import { type ProjectRow } from "@nexys/user-management-client";
import {
  AlertCircle,
  ArrowUpDown,
  Boxes,
  ListFilter,
  RefreshCcw,
  Search,
} from "lucide-react";
import { useMemo, useState } from "react";

import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";
import { ProjectCreateDialog } from "./project-create-dialog.js";

const SORT_OPTIONS: {
  value: string;
  label: string;
  compare: (a: ProjectRow, b: ProjectRow) => number;
}[] = [
  {
    value: "name-asc",
    label: "Name A–Z",
    compare: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  },
  {
    value: "name-desc",
    label: "Name Z–A",
    compare: (a, b) => b.name.localeCompare(a.name, undefined, { sensitivity: "base" }),
  },
  { value: "created-desc", label: "Newest first", compare: (a, b) => b.createdAt.localeCompare(a.createdAt) },
  { value: "created-asc", label: "Oldest first", compare: (a, b) => a.createdAt.localeCompare(b.createdAt) },
  { value: "tenants-desc", label: "Most tenants", compare: (a, b) => (b.tenantCount ?? 0) - (a.tenantCount ?? 0) },
];

const SIGNUP_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All sign-up" },
  { value: "open", label: "Sign-up open" },
  { value: "closed", label: "Sign-up closed" },
];

export const ProjectsTable = ({
  allowCreate = true,
  onSelect,
}: {
  allowCreate?: boolean;
  onSelect?: (id: string) => void;
}) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("name-asc");
  const [signupFilter, setSignupFilter] = useState("all");
  const { data, error, loading, refresh } = useCall("listProjects");

  const filtered = useMemo(() => {
    let list = data?.projects ?? [];
    if (signupFilter !== "all") {
      list = list.filter((p) => p.allowSignup === (signupFilter === "open"));
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q)
      );
    }
    const compare = (SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0]!)
      .compare;
    return [...list].sort(compare);
  }, [data, query, signupFilter, sort]);

  const filterActive = signupFilter !== "all";

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-[14rem] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={signupFilter} onValueChange={setSignupFilter}>
            <SelectTrigger
              className="h-8 w-[10rem]"
              aria-label="Filter by sign-up"
            >
              <ListFilter className="size-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIGNUP_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-8 w-[9.5rem]" aria-label="Sort by">
              <ArrowUpDown className="size-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCcw />
            Refresh
          </Button>
          {allowCreate ? <ProjectCreateDialog onCreated={refresh} /> : null}
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      ) : null}

      <div className="overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Tenancy</TableHead>
              <TableHead>Sign-up</TableHead>
              <TableHead>Tenants</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-10" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="p-0">
                  <EmptyState
                    icon={Boxes}
                    title={
                      query || filterActive ? "No products match" : "No products yet"
                    }
                    description={
                      query || filterActive
                        ? "Try a different search or filter."
                        : "Create the first product to get started."
                    }
                    className="border-0 rounded-none bg-transparent"
                    action={
                      !query && !filterActive && allowCreate ? (
                        <ProjectCreateDialog onCreated={refresh} />
                      ) : undefined
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow
                  key={p.id}
                  className={onSelect ? "cursor-pointer" : undefined}
                  onClick={onSelect ? () => onSelect(p.id) : undefined}
                >
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 items-center justify-center rounded-md bg-muted text-xs font-semibold uppercase">
                        {p.name.slice(0, 2)}
                      </div>
                      <span className="font-medium">{p.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{p.slug}</TableCell>
                  <TableCell>
                    <Badge variant="muted" className="capitalize">
                      {p.tenancyMode}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={p.allowSignup ? "default" : "muted"}>
                      {p.allowSignup ? "open" : "closed"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="muted">{p.tenantCount ?? 0}</Badge>
                  </TableCell>
                  <TableCell title={p.createdAt} className="text-sm text-muted-foreground">
                    {formatRelative(p.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
